import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Button, CircularProgress, Fab, IconButton, Paper, TextField, Tooltip } from '@mui/material'
import { MdAdd, MdCheckCircle, MdClose, MdErrorOutline, MdForum, MdSend, MdSupportAgent } from 'react-icons/md'
import { useTranslation } from 'react-i18next'
import { CurrentPortalContext } from '../../context/Shared/CurrentPortal.context'
import { CurrentUserContext } from '../../context/Shared/CurrentUser.context'
import SupportService from '../../services/Support/Support.service'
import { runBrowserDiagnostics } from '../../utils/browserDiagnostics'
import { SUPPORT_OPEN_EVENT } from '../../utils/supportChat'
import { hideSalesIQButton, showSalesIQButton, startHumanChat, watchOperatorsOnline } from '../../utils/salesIQ'
import styles from './SupportChatWidget.module.scss'

/**
 * Floating in-app support chat. The agent runs on the backend (/support/chat); this
 * widget renders its stream, asks the customer to approve changes, and runs browser
 * diagnostics when the agent requests them.
 *
 * In a call the floating button is hidden (hideLauncher) and the panel is opened from the
 * call's More menu instead; the agent is told which call the customer is in.
 *
 * While this widget is mounted the Zoho SalesIQ button is hidden. When the agent
 * escalates, a card offers a live chat with the TeleVU team that opens SalesIQ with the
 * agent's summary as the first message.
 */
const SupportChatWidget = ({ socketConnected, currentCallId = null, hideLauncher = false }) => {
  const { t, i18n } = useTranslation()
  const { portal } = useContext(CurrentPortalContext)
  const { me } = useContext(CurrentUserContext)
  const [operatorsOnline, setOperatorsOnline] = useState(null)
  const [humanChatFailed, setHumanChatFailed] = useState(false)
  const [open, setOpen] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [conversationId, setConversationId] = useState(null)
  const [items, setItems] = useState([])
  const [pendingActions, setPendingActions] = useState([])
  const [approvals, setApprovals] = useState({})
  const [clientResults, setClientResults] = useState({})
  const [input, setInput] = useState('')
  const [streaming, setStreaming] = useState(false)
  const abortRef = useRef(null)
  const endRef = useRef(null)
  const conversationIdRef = useRef(null)
  conversationIdRef.current = conversationId
  const startedChecksRef = useRef(new Set())

  // One way in for signed-in users: SalesIQ only appears when the agent hands off.
  useEffect(() => {
    hideSalesIQButton()
    const stopWatching = watchOperatorsOnline(setOperatorsOnline)
    return () => {
      stopWatching()
      showSalesIQButton()
    }
  }, [])

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [items, pendingActions])

  // Switching portal starts a fresh widget; conversations are per portal.
  useEffect(() => {
    abortRef.current?.abort()
    setLoaded(false)
    setConversationId(null)
    setItems([])
    setPendingActions([])
  }, [portal.name])

  const handleEvent = useCallback((event) => {
    switch (event.type) {
      case 'start':
        setConversationId(event.conversationId)
        break
      case 'token':
        setItems(prev => {
          const last = prev[prev.length - 1]
          if (last?.role === 'assistant' && last.live) {
            return [...prev.slice(0, -1), { ...last, text: last.text + event.content }]
          }
          return [...prev, { role: 'assistant', text: event.content, live: true }]
        })
        break
      case 'tool_start':
        setItems(prev => [...prev, { role: 'tool', id: event.id, label: event.label, status: 'running' }])
        break
      case 'tool_end':
        setItems(prev => prev.map(i => (i.role === 'tool' && i.id === event.id ? { ...i, status: event.ok ? 'done' : 'error' } : i)))
        break
      case 'handoff':
        setItems(prev => [...prev, { role: 'handoff', id: event.id, summary: event.summary }])
        break
      case 'action_required':
        setPendingActions(event.actions)
        break
      case 'error':
        setItems(prev => [...prev, { role: 'error', text: event.message }])
        break
      default:
        break
    }
  }, [])

  const send = useCallback(async (payload) => {
    setStreaming(true)
    setPendingActions([])
    setApprovals({})
    setClientResults({})
    const controller = new AbortController()
    abortRef.current = controller
    try {
      await SupportService.streamChat(
        { portal: portal.name, conversationId: conversationIdRef.current, currentCallId, language: i18n.language, ...payload },
        { onEvent: handleEvent, signal: controller.signal }
      )
    } catch (e) {
      if (e.name !== 'AbortError') {
        setItems(prev => [...prev, {
          role: 'error',
          text: e.status === 429 ? t('Too many messages. Please wait a moment.') : t('Could not reach support. Please try again.')
        }])
      }
    } finally {
      // Tool lines left "running" (e.g. after an abort) should not spin forever.
      setItems(prev => prev.map(i => ({ ...i, live: false, status: i.status === 'running' ? 'done' : i.status })))
      setStreaming(false)
    }
  }, [portal.name, handleEvent, t, currentCallId, i18n.language])

  // Browser checks run as soon as the agent asks. The turn is sent back once every
  // pending action (checks and approvals) has an answer.
  useEffect(() => {
    pendingActions
      .filter(a => a.kind === 'client' && !startedChecksRef.current.has(a.toolUseId))
      .forEach(async (action) => {
        startedChecksRef.current.add(action.toolUseId)
        const result = await runBrowserDiagnostics({ testMedia: action.input?.testMedia, socketConnected })
        setClientResults(prev => ({ ...prev, [action.toolUseId]: result }))
      })
  }, [pendingActions, socketConnected])

  useEffect(() => {
    if (streaming || pendingActions.length === 0) return
    const answered = pendingActions.every(a => (a.kind === 'client' ? a.toolUseId in clientResults : a.toolUseId in approvals))
    if (answered) send({ approvals, clientResults })
  }, [pendingActions, approvals, clientResults, streaming, send])

  const openWidget = async () => {
    setOpen(true)
    if (loaded) return
    setLoaded(true)
    try {
      const { data } = await SupportService.getLatestConversation(portal.name)
      if (data) {
        setConversationId(data.id)
        setItems(data.items.map(i => (i.role === 'tool' ? { ...i, status: 'done' } : i)))
        setPendingActions(data.pendingActions)
      }
    } catch { /* start with an empty chat */ }
  }

  const openWidgetRef = useRef(openWidget)
  openWidgetRef.current = openWidget
  useEffect(() => {
    const onOpen = () => openWidgetRef.current()
    window.addEventListener(SUPPORT_OPEN_EVENT, onOpen)
    return () => window.removeEventListener(SUPPORT_OPEN_EVENT, onOpen)
  }, [])

  const submitMessage = (e) => {
    e?.preventDefault()
    const message = input.trim()
    if (!message || streaming) return
    setInput('')
    setItems(prev => [...prev, { role: 'user', text: message }])
    // Typing while actions are pending answers the ones decided so far; the agent is
    // told the rest went unanswered.
    send({ message, approvals, clientResults })
  }

  const newChat = async () => {
    abortRef.current?.abort()
    if (conversationId) {
      SupportService.closeConversation(conversationId, portal.name).catch(() => {})
    }
    setConversationId(null)
    setItems([])
    setPendingActions([])
  }

  const openHumanChat = (summary) => {
    // Must run inside the click: Zoho only starts a chat from a user gesture.
    const started = startHumanChat({ name: me?.name, email: me?.email, portal: portal.name, summary, conversationId })
    setHumanChatFailed(!started)
    if (started) setOpen(false)
  }

  const handoff = [...items].reverse().find(i => i.role === 'handoff')
  const confirmActions = pendingActions.filter(a => a.kind === 'confirm')
  const runningChecks = pendingActions.some(a => a.kind === 'client' && !(a.toolUseId in clientResults))

  if (!open) {
    if (hideLauncher) return null
    return (
      <div className={styles.fabWrapper}>
        <Tooltip title={t('Help & support')} placement='left'>
          <Fab className={styles.fab} onClick={openWidget} aria-label={t('Help & support')}>
            <MdSupportAgent size={28} />
          </Fab>
        </Tooltip>
      </div>
    )
  }

  return (
    <Paper className={styles.panel} elevation={8}>
      <div className={styles.header}>
        <MdSupportAgent size={22} />
        <span className={styles.title}>{t('TeleVU Support')}</span>
        <Tooltip title={t('New chat')}>
          <span>
            <IconButton size='small' onClick={newChat} disabled={!conversationId} className={styles.headerButton}>
              <MdAdd />
            </IconButton>
          </span>
        </Tooltip>
        <IconButton size='small' onClick={() => setOpen(false)} className={styles.headerButton} aria-label={t('Close')}>
          <MdClose />
        </IconButton>
      </div>

      <div className={styles.messages} aria-live='polite' aria-busy={streaming || runningChecks}>
        {items.length === 0 && (
          <div className={styles.empty}>
            {t('Hi! Tell me what is going wrong and I will look into your portal. For example: "My glasses are not getting calls" or "My camera does not work in calls".')}
          </div>
        )}
        {items.map((item, idx) => {
          if (item.role === 'handoff') return null
          if (item.role === 'tool') {
            return (
              <div key={item.id || idx} className={styles.tool}>
                {item.status === 'running' && <CircularProgress size={12} />}
                {item.status === 'done' && <MdCheckCircle className={styles.toolDone} />}
                {item.status === 'error' && <MdErrorOutline className={styles.toolError} />}
                <span>{t(item.label)}</span>
              </div>
            )
          }
          return (
            <div key={idx} className={`${styles.bubble} ${styles[item.role]}`}>
              {item.text}
            </div>
          )
        })}

        {/* Pinned under the agent's reply, which points the customer to it. */}
        {handoff && !streaming && (
          <div className={styles.actionCard}>
            <div className={styles.actionTitle}>{t('Talk to the TeleVU team')}</div>
            <div className={styles.actionDetail}>
              {operatorsOnline === false
                ? t('Nobody from the team is online right now. Leave them a message and they will follow up.')
                : t('A member of the TeleVU team can take it from here. They will see a summary of this chat, so you will not need to repeat yourself.')}
            </div>
            {humanChatFailed && (
              <div className={styles.actionDetail}>
                {t('The live chat could not open in this browser. You can book a session with the TeleVU team at https://booking.televu.ca/#/support')}
              </div>
            )}
            <div className={styles.actionButtons}>
              <Button size='small' variant='contained' startIcon={<MdForum />} onClick={() => openHumanChat(handoff.summary)}>
                {operatorsOnline === false ? t('Leave a message') : t('Chat with a person')}
              </Button>
            </div>
          </div>
        )}

        {confirmActions.map(action => (
          <div key={action.toolUseId} className={styles.actionCard}>
            <div className={styles.actionTitle}>{action.title}</div>
            {action.reason && <div className={styles.actionReason}>{action.reason}</div>}
            <div className={styles.actionDetail}>{action.detail}</div>
            {action.toolUseId in approvals
              ? <div className={styles.actionDecision}>{approvals[action.toolUseId] ? t('Approved') : t('Declined')}</div>
              : (
              <div className={styles.actionButtons}>
                <Button size='small' variant='outlined' onClick={() => setApprovals(prev => ({ ...prev, [action.toolUseId]: false }))}>
                  {t('Decline')}
                </Button>
                <Button size='small' variant='contained' onClick={() => setApprovals(prev => ({ ...prev, [action.toolUseId]: true }))}>
                  {t('Approve')}
                </Button>
              </div>
                )}
          </div>
        ))}

        {runningChecks && (
          <div className={styles.tool}>
            <CircularProgress size={12} />
            <span>{t('Running checks in your browser')}</span>
          </div>
        )}
        {streaming && !items[items.length - 1]?.live && items[items.length - 1]?.status !== 'running' && (
          <div className={styles.typing}><span /><span /><span /></div>
        )}
        <div ref={endRef} />
      </div>

      <form className={styles.inputRow} onSubmit={submitMessage}>
        <TextField
          fullWidth
          size='small'
          multiline
          maxRows={4}
          placeholder={t('Describe the problem...')}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) submitMessage(e)
          }}
          inputProps={{ maxLength: 4000 }}
        />
        <IconButton type='submit' disabled={!input.trim() || streaming} color='primary' aria-label={t('Send')}>
          <MdSend />
        </IconButton>
      </form>
      <div className={styles.footer}>{t('AI assistant. Please do not share patient information.')}</div>
    </Paper>
  )
}

export default SupportChatWidget
